define((require) => {
    "use strict";

    const {
        isEmpty, defaults, compose, clone, identity, chain
    } = require("underscore");

    const _defaults = {
        primaryIndex: 0,
        margin: 5,
        containerWidth: 185,
        getFitboxOnly: true,
        fitboxDimensions: {
            width: 130,
            height: 70
        }
    };

    return {

        _sortFn(item) {
            return -(item.editedAt || item.createdAt);
        },

        _accumulateWidths(items) {
            return items.reduce((accu, item) => accu + item.fitbox.width, 0);
        },

        /**
         * Returns a function that adds a fitbox property to a supplied dimension. fitbox.width and
         * fitbox.height are calculated so that the item is as big as possible, while still
         * fitting inside the supplied fitboxDimensions.
         *
         * @private
         * @param {Object} fitboxDimensions Width and height that the dimensions should fit into.
         * @returns {Function}
         */
        _getApplyScale(fitboxDimensions) {
            return (item) => {
                const itemWidth = item.width;
                const itemHeight = item.height;
                const widthScale = fitboxDimensions.width / itemWidth;
                const heightScale = fitboxDimensions.height / itemHeight;
                const scalar = (widthScale < heightScale) ?
                    fitboxDimensions.width / itemWidth :
                    fitboxDimensions.height / itemHeight;

                item.fitbox = {
                    width:  Math.round(itemWidth * scalar),
                    height: Math.round(itemHeight * scalar)
                };

                return item;
            };
        },

        /**
         * Returns the overlap and margin values needed to offset the thumbnails. One of the two
         * values will always be zero.
         *
         * @private
         * @param {Number} containerWidth   The width availble for the thumbnails.
         * @param {Number} itemsWidth       The total width of all the thumbnails.
         * @param {Number} numberOfItems    The number of thumbnails.
         * @param {Number} primaryItemWidth The width of the primary thumbnail.
         * @param {Number} maximumMargin    The maximum allowed margin between thumbnails when
                                            tiling them.
         * @returns {Object} Return an object with an overlap and a margin property.
         */
        _getOverlapAndMargin(containerWidth, itemsWidth, numberOfItems, primaryItemWidth, maximumMargin) {
            // How much will the items overflow if we tile as close as possible, i.e. without
            // any margin?
            const overflow = itemsWidth - containerWidth;

            // 0 = No effect.
            let overlap = 0;
            let margin = 0;

            if (overflow > 0) {
                const leftOverSpace = containerWidth - primaryItemWidth;
                overlap = overflow / (leftOverSpace + overflow);
            } else {
                // We're tiling the thumbnails next to each other. Calculate how much margin
                // between them.
                margin = (containerWidth - itemsWidth) / (numberOfItems - 1);

                if (margin > maximumMargin) {
                    margin = maximumMargin;
                }
            }

            return {overlap, margin};
        },

        /**
         * Returns a function that sets .fitbox.x on the supplied item. Expects that either overlap
         * or margin is 0.
         *
         * @private
         * @param {Number} overlap  How much each thumbnail should overlap, as a relative value
         *                          from 0 to 1. Settings this to 0 turns off overlapping.
         * @param {Number} margin   How much margins to have between each thumbnail. Setting this
         *                          to 0 turns off margins.
         * @returns {Function}
         */
        _getApplyOffset(overlap, margin) {
            let accumulatedOffset = 0;

            return (item, index) => {
                item.fitbox.x = accumulatedOffset;
                // Don't apply overlapping to the first item.
                if (index > 0) {
                    item.fitbox.x -= overlap * item.fitbox.width;
                }

                // Update the offset for the next item.
                accumulatedOffset = item.fitbox.x + item.fitbox.width + margin;

                return item;
            };
        },

        _getFitbox(item) {
            return item.fitbox;
        },

        /**
         * calculateLayout
         *
         * @param   {Object[]}  dimensions
         * @param   {Object}    [options]
         * @return  {Object[]}
         */
        calculateLayout(dimensions, options) {
            if (isEmpty(dimensions)) {
                return [];
            }

            // Assigning simple values.
            const opt = defaults({}, options, _defaults);

            // Cloning and scaling the array of dimensions.
            const applyScale    = this._getApplyScale(opt.fitboxDimensions);
            const cloneAndScale = compose(applyScale, clone);
            const items         = dimensions.map(cloneAndScale);

            // We need the primary to calculate offsets
            const primary = items[opt.primaryIndex];

            // Is there enough space or do we need to stack the thumbnails?
            const itemsWidth = this._accumulateWidths(items);

            const overlapAndMargin = this._getOverlapAndMargin(
                opt.containerWidth,
                itemsWidth,
                items.length,
                primary.fitbox.width,
                opt.margin
            );

            const applyOffset = this._getApplyOffset(overlapAndMargin.overlap, overlapAndMargin.margin);

            // We have the option to only return the fitbox, rather than
            // the entire object.
            const getFitbox = opt.getFitboxOnly ? this._getFitbox : identity;

            return chain(items)     // Begin Chain.
                .sortBy(this._sortFn) // Sort them.
                .without(primary)     // Move the primary to...
                .unshift(primary)     // ... the front.
                .map(applyOffset)     // Apply the offset.
                .reverse()            // Reverse so they come out in rendering order.
                .map(getFitbox)       // Return just the fitbox if requested.
                .value();             // End chain.
        }
    };
});



// WEBPACK FOOTER //
// ./src/editor/util/ThumbnailLayouter.js